import React, { useState } from 'react';
import Button from '../../../../../shared/components/ui/Button';

const Section4 = ({
  mode,
  collapsedSections,
  renderSectionHeader,
  handleOpenReasonModal,
  dsoData,
  month,
  canAddReason,
  allReasonsData,
  isLoadingReasons,
  onSubmitCommentInline,
  isSubmittingComment,
  sectionNote
}) => {
  // Filter reasons for Visit To Booking Conversion topic
  const section4Reasons = Array.isArray(allReasonsData)
    ? allReasonsData.filter((reason) => reason.topic === 'Visit To Booking Conversion')
    : [];

  // Get the global reason for the section (first Visit To Booking Conversion reason)
  const globalReason = section4Reasons.length > 0 ? section4Reasons[0] : null;

  // State for inline editing
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [commentText, setCommentText] = useState(globalReason?.reason || '');

  // Track previous submitting state to detect when submission completes
  const prevIsSubmittingRef = React.useRef(isSubmittingComment);

  // Update commentText when globalReason changes and reset editing state after successful save
  React.useEffect(() => {
    if (globalReason?.reason) {
      setCommentText(globalReason.reason);
    } else {
      setCommentText('');
    }

    // Reset editing state when submission completes successfully
    if (prevIsSubmittingRef.current && !isSubmittingComment && isEditingComment) {
      setIsEditingComment(false);
    }
    prevIsSubmittingRef.current = isSubmittingComment;
  }, [globalReason?.reason, isSubmittingComment, isEditingComment]);

  const handleStartEditing = () => {
    setIsEditingComment(true);
    setCommentText(globalReason?.reason || '');
  };

  const handleCancelEditing = () => {
    setIsEditingComment(false);
    setCommentText(globalReason?.reason || '');
  };

  const handleSaveComment = () => {
    if (!commentText.trim()) {
      return;
    }
    // Pass reason ID if editing existing comment
    const reasonId = globalReason?.id || null;
    onSubmitCommentInline('Visit To Booking Conversion', commentText, reasonId);
    // Editing state will be reset in useEffect after successful save
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {renderSectionHeader(4, 'Visits to Booking Conversion %')}
      {(mode === 'view' || !collapsedSections[4]) && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {(() => {
                  if (!month) return 'Last Month';
                  const currentYear = new Date().getFullYear();
                  let lastMonthNumber, lastMonthYear;
                  if (month === 1) {
                    lastMonthNumber = 12;
                    lastMonthYear = currentYear - 1;
                  } else {
                    lastMonthNumber = month - 1;
                    lastMonthYear = currentYear;
                  }
                  // Create a date object with the month number (month is 0-indexed in Date, so subtract 1)
                  const lastMonthDate = new Date(lastMonthYear, lastMonthNumber - 1, 1);
                  return lastMonthDate.toLocaleString('default', { month: 'long' });
                })()}{' '}
                {(() => {
                  if (!month) return new Date().getFullYear();
                  // Show correct year for previous month
                  if (month === 1) {
                    return new Date().getFullYear() - 1;
                  }
                  return new Date().getFullYear();
                })()}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Total Visits</p>
                  <p className=" font-bold text-gray-900">
                    {dsoData?.data?.visitActivityLast || 0}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Bookings</p>
                  <p className=" font-bold text-gray-900">{dsoData?.data?.dealActivityLast || 0}</p>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {dsoData?.data?.conversionRatioLast || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">
                {(() => {
                  if (!month) return 'Current Month';
                  const currentMonthDate = new Date(new Date().getFullYear(), month - 1, 1);
                  return currentMonthDate.toLocaleString('default', { month: 'long' });
                })()}{' '}
                {new Date().getFullYear()}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Total Visits</p>
                  <p className=" font-bold text-gray-900">
                    {dsoData?.data?.visitActivityCurrent || 0}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600">Bookings</p>
                  <p className=" font-bold text-gray-900">
                    {dsoData?.data?.dealActivityCurrent || 0}
                  </p>
                </div>
                <div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-600">Conversion Rate</p>
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      {dsoData?.data?.conversionRatioCurrent || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comment Section - Only show if current conversion ratio is smaller than last */}
          {(() => {
            const conversionRatioCurrent = dsoData?.data?.conversionRatioCurrent || 0;
            const conversionRatioLast = dsoData?.data?.conversionRatioLast || 0;

            // Only show comment section if current ratio is smaller than last
            if (conversionRatioCurrent >= conversionRatioLast) {
              return null;
            }

            return (
              <div className="mt-6 p-5 bg-gradient-to-r from-red-50 to-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <svg
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h4 className="text-sm font-semibold text-gray-900">
                        {globalReason?.user?.vertical || 'Sales'} comment
                      </h4>
                      {globalReason?.user && !isEditingComment && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                          <span className="text-xs text-gray-600">By</span>
                          <span className="text-xs font-medium text-gray-900">
                            {globalReason.user.name || 'Unknown User'}
                          </span>
                        </div>
                      )}
                    </div>
                    {isLoadingReasons ? (
                      <p className="text-sm text-gray-500 italic">Loading comment...</p>
                    ) : isEditingComment ? (
                      <div className="space-y-3">
                        <textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Enter your comment..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                          rows={4}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSaveComment}
                            displayType="secondary-red"
                            disabled={!commentText.trim() || isSubmittingComment}>
                            {isSubmittingComment ? 'Saving...' : 'Save'}
                          </Button>
                          <Button
                            onClick={handleCancelEditing}
                            displayType="secondary"
                            disabled={isSubmittingComment}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : globalReason ? (
                      <div
                        onClick={
                          canAddReason && mode === 'dashboard' ? handleStartEditing : undefined
                        }
                        className={`text-sm text-gray-900 font-medium leading-relaxed ${
                          canAddReason && mode === 'dashboard'
                            ? 'cursor-pointer hover:bg-red-100 p-2 rounded transition-colors'
                            : ''
                        }`}>
                        {globalReason.reason}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No comment added yet</p>
                    )}
                  </div>
                  {/* Add Comment Button - always show when canAddReason is true and mode is dashboard */}
                  {canAddReason && mode === 'dashboard' && !isEditingComment && (
                    <div className="flex-shrink-0">
                      <Button
                        onClick={
                          globalReason ? handleStartEditing : () => setIsEditingComment(true)
                        }
                        displayType="secondary-red">
                        {globalReason ? 'Edit Comment' : 'Add Comment'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}
      {/* Section Note */}
      {sectionNote && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  {sectionNote.user?.vertical || 'Management'} notes
                </h4>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">By</span>
                  <span className="text-xs font-medium text-gray-900">
                    {sectionNote.user?.name || 'Unknown User'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{sectionNote.note}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section4;
